import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IconArrowLeft, IconSend } from "@tabler/icons-react";
import { mutate } from "swr";
import { mutateByPrefix } from "../../../../swr/useApi";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Select } from "../../../../components/forms/select";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { DisputeStatusChip } from "../components/dispute-status-chip";
import { DISPUTE_REASON_LABEL } from "../components/dispute-labels";
import {
  useAdminOrderDisputeDetails,
  resolveOrderDispute,
  addAdminOrderDisputeMessage,
  ALLOWED_TRANSITIONS,
} from "../../hooks/useAdminOrderDisputes";
import type { OrderDisputeMessageDto, OrderDisputeDtoStatusEnum, ResolveDisputeDto } from "../../../../api";
import { ResolveDisputeDtoResolutionActionEnum } from "../../../../api";

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function formatDate(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  const d = iso instanceof Date ? iso : new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DATE_FMT.format(d);
}

const STATUS_LABEL: Record<string, string> = {
  OPEN: "Open",
  UNDER_REVIEW: "Under Review",
  RESOLVED_REFUND: "Resolved (Refund)",
  RESOLVED_REPLACEMENT: "Resolved (Replacement)",
  RESOLVED_OTHER: "Resolved (Other)",
  REJECTED: "Rejected",
};

// Statuses that imply a fixed resolution action — no admin choice needed.
const STATUS_RESOLUTION_ACTION: Partial<Record<string, ResolveDisputeDtoResolutionActionEnum>> = {
  RESOLVED_REFUND: ResolveDisputeDtoResolutionActionEnum.Refund,
  RESOLVED_REPLACEMENT: ResolveDisputeDtoResolutionActionEnum.Replacement,
  REJECTED: ResolveDisputeDtoResolutionActionEnum.None,
};

const OTHER_RESOLUTION_ACTION_OPTIONS = [
  { value: ResolveDisputeDtoResolutionActionEnum.Credit, label: "Credit" },
  { value: ResolveDisputeDtoResolutionActionEnum.None, label: "None" },
];

export function AdminOrderDisputeDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const cacheKey = id ? `admin-order-dispute-${id}` : null;
  const { data: dispute, isLoading } = useAdminOrderDisputeDetails(id ?? null);

  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const [pendingStatus, setPendingStatus] = useState<OrderDisputeDtoStatusEnum | null>(null);
  const [resolutionAction, setResolutionAction] = useState<ResolveDisputeDtoResolutionActionEnum | "">("");
  const [resolutionNote, setResolutionNote] = useState("");
  const [resolving, setResolving] = useState(false);

  async function handleSendMessage() {
    if (!id || !replyText.trim()) return;
    setSending(true);
    try {
      await addAdminOrderDisputeMessage(id, { content: replyText.trim() });
      setReplyText("");
      await mutate(cacheKey);
    } finally {
      setSending(false);
    }
  }

  function startTransition(status: OrderDisputeDtoStatusEnum) {
    setPendingStatus(status);
    setResolutionAction(STATUS_RESOLUTION_ACTION[status] ?? "");
    setResolutionNote("");
  }

  async function confirmTransition() {
    if (!id || !pendingStatus) return;
    setResolving(true);
    try {
      const dto: ResolveDisputeDto = {
        status: pendingStatus,
        ...(resolutionAction ? { resolutionAction } : {}),
        ...(resolutionNote.trim() ? { resolutionNote: resolutionNote.trim() } : {}),
      };
      const updated = await resolveOrderDispute(id, dto);
      if (updated) {
        setPendingStatus(null);
        await mutate(cacheKey);
        await mutateByPrefix("admin-order-disputes");
        await mutateByPrefix("admin-order-dispute-stats");
      }
    } finally {
      setResolving(false);
    }
  }

  if (isLoading || !dispute) {
    return (
      <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-4 py-6 md:px-8 md:py-8">
        <ShimmerLoader message="Loading dispute details…" lines={8} />
      </div>
    );
  }

  const nextStatuses = ALLOWED_TRANSITIONS[dispute.status] ?? [];
  const messages = (dispute.messages ?? []) as OrderDisputeMessageDto[];

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-4 py-6 md:px-8 md:py-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(AppRoutes.ADMIN_ORDER_DISPUTES)}
          className="flex items-center gap-1 text-textSecondary hover:text-textDark transition-colors"
        >
          <IconArrowLeft size={18} />
          <Text variant="bodySm" tone="body">
            Back to disputes
          </Text>
        </button>
      </div>

      <div className="flex items-start justify-between">
        <div>
          <Heading variant="h2" tone="dark">
            Order Dispute
          </Heading>
          <Text variant="bodySm" tone="muted" className="font-mono mt-0.5">
            {dispute.id}
          </Text>
        </div>
        <DisputeStatusChip status={dispute.status} />
      </div>

      {/* Dispute info */}
      <div className="rounded-2xl border border-borderDefault bg-bgWhite p-6 flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Text variant="bodySmBold" tone="dark">
              Reason
            </Text>
            <Text variant="body" tone="body">
              {DISPUTE_REASON_LABEL[dispute.reason] ?? dispute.reason}
            </Text>
          </div>
          <div>
            <Text variant="bodySmBold" tone="dark">
              Sale Order
            </Text>
            <Text variant="body" tone="body">
              #{dispute.saleOrderId}
            </Text>
          </div>
          <div>
            <Text variant="bodySmBold" tone="dark">
              Filed
            </Text>
            <Text variant="body" tone="body">
              {formatDate(dispute.createdAt)}
            </Text>
          </div>
          <div>
            <Text variant="bodySmBold" tone="dark">
              Last Updated
            </Text>
            <Text variant="body" tone="body">
              {formatDate(dispute.updatedAt)}
            </Text>
          </div>
        </div>
        <div>
          <Text variant="bodySmBold" tone="dark">
            Description
          </Text>
          <Text variant="body" tone="body" className="mt-1">
            {dispute.description}
          </Text>
        </div>
        {dispute.attachments.length > 0 && (
          <div>
            <Text variant="bodySmBold" tone="dark">
              Attachments
            </Text>
            <div className="mt-1 flex flex-col gap-1">
              {dispute.attachments.map((att) => (
                <Text
                  key={att.objectKey}
                  variant="bodySm"
                  tone="muted"
                  className="font-mono"
                >
                  {att.fileName || att.objectKey.split("/").pop()}
                </Text>
              ))}
            </div>
          </div>
        )}
        {(dispute.resolutionAction || dispute.resolutionNote) && (
          <div className="grid grid-cols-2 gap-4 border-t border-borderDefault pt-4">
            {dispute.resolutionAction && (
              <div>
                <Text variant="bodySmBold" tone="dark">
                  Resolution Action
                </Text>
                <Text variant="body" tone="body">
                  {dispute.resolutionAction}
                </Text>
              </div>
            )}
            {dispute.resolutionNote && (
              <div>
                <Text variant="bodySmBold" tone="dark">
                  Resolution Note
                </Text>
                <Text variant="body" tone="body">
                  {dispute.resolutionNote}
                </Text>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Resolution actions */}
      {nextStatuses.length > 0 && (
        <div className="rounded-2xl border border-borderDefault bg-bgWhite p-4 flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <Text variant="bodySm" tone="body">
              Update status:
            </Text>
            {nextStatuses.map((s) => (
              <Button
                key={s}
                variant={pendingStatus === s ? "primary" : "secondary"}
                size="sm"
                disabled={resolving}
                onClick={() => startTransition(s)}
              >
                {STATUS_LABEL[s] ?? s}
              </Button>
            ))}
          </div>

          {pendingStatus && (
            <div className="flex flex-col gap-3 border-t border-borderDefault pt-3">
              {pendingStatus === "RESOLVED_OTHER" && (
                <Select
                  name="resolutionAction"
                  label="Resolution action"
                  value={resolutionAction}
                  onChange={(e) =>
                    setResolutionAction(e.target.value as ResolveDisputeDtoResolutionActionEnum)
                  }
                  options={OTHER_RESOLUTION_ACTION_OPTIONS}
                  density="dense"
                />
              )}
              <div className="flex flex-col gap-1.5">
                <Text variant="bodySmBold" tone="body">
                  Resolution note (optional)
                </Text>
                <textarea
                  value={resolutionNote}
                  onChange={(e) => setResolutionNote(e.target.value)}
                  placeholder="Add a note for the buyer…"
                  rows={2}
                  className="w-full rounded-xl border border-borderDefault bg-bgLight px-4 py-3 text-sm text-textDark placeholder:text-textMuted resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" size="sm" disabled={resolving} onClick={() => setPendingStatus(null)}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" disabled={resolving} onClick={confirmTransition}>
                  {resolving ? "Saving…" : `Confirm ${STATUS_LABEL[pendingStatus] ?? pendingStatus}`}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Message thread */}
      <div className="rounded-2xl border border-borderDefault bg-bgWhite flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-borderDefault">
          <Heading variant="h3" tone="dark">
            Message Thread
          </Heading>
        </div>

        {messages.length === 0 ? (
          <div className="px-6 py-8 flex items-center justify-center">
            <Text variant="body" tone="muted">
              No messages yet. Add the first response below.
            </Text>
          </div>
        ) : (
          <div className="flex flex-col divide-y divide-borderDefault">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`px-6 py-4 ${m.authorRole === "admin" ? "bg-accentCyanTint/30" : ""}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Text variant="bodySmBold" tone="dark">
                    {m.authorRole === "admin" ? "You (Admin)" : "Buyer"}
                  </Text>
                  <Text variant="bodyXs" tone="muted">
                    {formatDate(m.createdAt)}
                  </Text>
                </div>
                <Text variant="body" tone="body">
                  {m.content}
                </Text>
              </div>
            ))}
          </div>
        )}

        {/* Reply box */}
        <div className="px-6 py-4 border-t border-borderDefault flex flex-col gap-3">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write a response to the buyer…"
            rows={3}
            className="w-full rounded-xl border border-borderDefault bg-bgLight px-4 py-3 text-sm text-textDark placeholder:text-textMuted resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <div className="flex justify-end">
            <Button
              variant="primary"
              size="sm"
              leftIcon={<IconSend size={15} />}
              disabled={!replyText.trim() || sending}
              onClick={handleSendMessage}
            >
              {sending ? "Sending…" : "Send Response"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminOrderDisputeDetailsPage;
